import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import { Box, LinearProgress } from "@material-ui/core";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import { fetchWorkerListPaging, getComplianceMatrixPage, setComplianceMatrixPage, getComplianceMatrixSearchKey, setComplianceMatrixSearchKey, getComplianceMatrixRagFilter, setComplianceMatrixRagFilter } from "store/slices/worker";
import { getAuthInfo } from "store/slices/auth";
import InputSearch from "components/Input/InputSearch";

const RAG = {
    GREEN: "green",
    AMBER: "amber",
    RED: "red",
    GREY: "grey",
};

const RAG_COLORS = {
    [RAG.GREEN]: { dot: "#16a34a", bg: "#dcfce7", text: "#166534" },
    [RAG.AMBER]: { dot: "#d97706", bg: "#fef3c7", text: "#92400e" },
    [RAG.RED]:   { dot: "#dc2626", bg: "#fee2e2", text: "#991b1b" },
    [RAG.GREY]:  { dot: "#9ca3af", bg: "#f3f4f6", text: "#6b7280" },
};

const useStyles = makeStyles(() => ({
    wrapper: {
        overflowX: "auto",
    },
    table: {
        borderCollapse: "collapse",
        fontSize: 12,
        minWidth: 1100,
        width: "100%",
    },
    th: {
        padding: "9px 10px",
        textAlign: "center",
        fontSize: 11,
        fontWeight: 600,
        color: "#6B7280",
        background: "#FAFAFA",
        borderBottom: "1px solid #E5E7EB",
        whiteSpace: "nowrap",
    },
    thSticky: {
        textAlign: "left",
        minWidth: 160,
        position: "sticky",
        left: 0,
        zIndex: 2,
        background: "#FAFAFA",
    },
    td: {
        padding: "10px 8px",
        borderBottom: "1px solid #F3F4F6",
        textAlign: "center",
        verticalAlign: "middle",
    },
    tdSticky: {
        textAlign: "left",
        position: "sticky",
        left: 0,
        zIndex: 1,
        background: "#fff",
        borderBottom: "1px solid #F3F4F6",
    },
    tdStickyRed: {
        background: "#fff8f8",
    },
    tdStickyAmber: {
        background: "#fffbf0",
    },
    avatar: {
        width: 28,
        height: 28,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
        fontWeight: 700,
        flexShrink: 0,
        background: "#CCFBF1",
        color: "#0D9488",
    },
    pill: {
        display: "inline-block",
        padding: "3px 9px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
    },
    dot: {
        width: 12,
        height: 12,
        borderRadius: "50%",
        display: "inline-block",
        marginBottom: 2,
    },
    cellLabel: {
        fontSize: 10,
        display: "block",
        marginTop: 2,
    },
    statCard: {
        borderRadius: 8,
        padding: "10px 14px",
        textAlign: "center",
        flex: 1,
        minWidth: 100,
    },
    statValue: {
        fontSize: 20,
        fontWeight: 700,
        display: "block",
    },
    statLabel: {
        fontSize: 11,
        display: "block",
        marginTop: 2,
    },
    filterRow: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        flexWrap: "wrap",
    },
    select: {
        padding: "7px 10px",
        border: "1px solid #E5E7EB",
        borderRadius: 6,
        fontSize: 12,
        color: "#374151",
        background: "#fff",
        cursor: "pointer",
    },
    infoBox: {
        background: "#EFF6FF",
        border: "1px solid #BFDBFE",
        borderRadius: 6,
        padding: "8px 12px",
        fontSize: 12,
        color: "#1D4ED8",
        marginBottom: 14,
        display: "flex",
        alignItems: "center",
        gap: 6,
    },
}));

const TRAINING_COLS = [
    { key: "workerHandHygiene",                          label: "Hand Hygiene" },
    { key: "workerMedicationManagement",                 label: "Medication Mgmt" },
    { key: "workerInfectionControl",                     label: "Infection Control" },
    { key: "workerSafeFoodHandling",                     label: "Safe Food Handling" },
    { key: "workerNdisOrientationModule",                label: "NDIS Orientation" },
    { key: "workerSupportingEffectiveCommunication",     label: "Effective Comm." },
    { key: "workerNdisSupportingSafeAndEnjoyableMeals",  label: "Safe Meals" },
    { key: "workerSupportingPeopleToStayInfectionFree",  label: "Infection Free" },
];

function getNdisRag(row) {
    if (!row.ndisExpireDate) return { rag: RAG.GREY, label: "—" };
    const diff = moment(row.ndisExpireDate).diff(moment(), "days");
    if (diff < 0)   return { rag: RAG.RED,   label: "Expired" };
    if (diff <= 90) return { rag: RAG.AMBER, label: `${diff}d left` };
    return { rag: RAG.GREEN, label: `exp ${moment(row.ndisExpireDate).format("YYYY")}` };
}

function getTrainingRag(data) {
    if (!data || !data.missing) return { rag: RAG.RED, label: "Missing" };
    if (data.expire) {
        const diff = moment(data.expire).diff(moment(), "days");
        if (diff < 0)   return { rag: RAG.RED,   label: "Expired" };
        if (diff <= 90) return { rag: RAG.AMBER, label: `${diff}d left` };
    }
    return { rag: RAG.GREEN, label: "Done" };
}

function getAppraisalRag(row) {
    if (!row.appraisalDueDate) return { rag: RAG.GREY, label: "—" };
    const diff = moment(row.appraisalDueDate).diff(moment(), "days");
    if (diff < 0)   return { rag: RAG.RED,   label: "Overdue" };
    if (diff <= 90) return { rag: RAG.AMBER, label: `${diff}d left` };
    return { rag: RAG.GREEN, label: moment(row.appraisalDueDate).format("MMM YYYY") };
}

function worstRag(rags) {
    if (rags.includes(RAG.RED))   return RAG.RED;
    if (rags.includes(RAG.AMBER)) return RAG.AMBER;
    if (rags.every(r => r === RAG.GREY)) return RAG.GREY;
    return RAG.GREEN;
}

function RagCell({ rag, label, classes }) {
    const c = RAG_COLORS[rag];
    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <span className={classes.dot} style={{ background: c.dot }} />
            <span className={classes.cellLabel} style={{ color: c.text }}>{label}</span>
        </div>
    );
}

function OverallPill({ rag, classes }) {
    const c = RAG_COLORS[rag];
    const labels = { [RAG.GREEN]: "Green", [RAG.AMBER]: "Amber", [RAG.RED]: "Red", [RAG.GREY]: "N/A" };
    return (
        <span className={classes.pill} style={{ background: c.bg, color: c.text }}>
            {labels[rag]}
        </span>
    );
}

function buildRowData(worker) {
    const ndis       = getNdisRag(worker);
    const trainings  = TRAINING_COLS.map(col => getTrainingRag(worker[col.key]));
    const appraisal  = getAppraisalRag(worker);
    const overall    = worstRag([ndis.rag, ...trainings.map(t => t.rag), appraisal.rag]);
    return { ndis, trainings, appraisal, overall };
}

const PAGE_SIZE = 5;

const ComplianceMatrixTab = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const authInfo = useSelector(getAuthInfo);
    const savedPage = useSelector(getComplianceMatrixPage);
    const savedSearchKey = useSelector(getComplianceMatrixSearchKey);
    const savedRagFilter = useSelector(getComplianceMatrixRagFilter);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchKey, setSearchKey] = useState(savedSearchKey);
    const [ragFilter, setRagFilter] = useState(savedRagFilter);
    const [page, setPageState] = useState(savedPage);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const setPage = (p) => {
        setPageState(p);
        dispatch(setComplianceMatrixPage(p));
    };

    const handleSearchKey = (val) => {
        setSearchKey(val);
        dispatch(setComplianceMatrixSearchKey(val));
    };

    const handleRagFilter = (val) => {
        setRagFilter(val);
        dispatch(setComplianceMatrixRagFilter(val));
    };

    useEffect(() => {
        loadData(savedPage, savedSearchKey, savedRagFilter);
    }, []);

    const loadData = async (p, search, rag) => {
        setLoading(true);
        const res = await dispatch(fetchWorkerListPaging({
            page: p,
            size: PAGE_SIZE,
            companyId: authInfo.companyId,
            search_key: search,
            sort_field: "createdDate",
            asc_sort: false,
        }));
        if (res.payload) {
            const content = res.payload.content ?? res.payload;
            setData(Array.isArray(content) ? content : []);
            setTotalElements(res.payload.totalElements ?? content.length);
            setTotalPages(res.payload.totalPages ?? 1);
        }
        setLoading(false);
    };

    const onSearch = (val) => {
        handleSearchKey(val);
        setPage(1);
        loadData(1, val, ragFilter);
    };

    const onRagFilter = (val) => {
        handleRagFilter(val);
        setPage(1);
        loadData(1, searchKey, val);
    };

    const goToPage = (p) => {
        setPage(p);
        loadData(p, searchKey, ragFilter);
    };

    const rows = data.map(w => ({ worker: w, ...buildRowData(w) }));

    const filtered = ragFilter
        ? rows.filter(r => r.overall === ragFilter)
        : rows;

    const totalItems = rows.length * (1 + TRAINING_COLS.length + 1);
    const countGreen = rows.reduce((n, r) => {
        const rags = [r.ndis.rag, ...r.trainings.map(t => t.rag), r.appraisal.rag];
        return n + rags.filter(x => x === RAG.GREEN).length;
    }, 0);
    const countAmber = rows.reduce((n, r) => {
        const rags = [r.ndis.rag, ...r.trainings.map(t => t.rag), r.appraisal.rag];
        return n + rags.filter(x => x === RAG.AMBER).length;
    }, 0);
    const countRed = rows.reduce((n, r) => {
        const rags = [r.ndis.rag, ...r.trainings.map(t => t.rag), r.appraisal.rag];
        return n + rags.filter(x => x === RAG.RED).length;
    }, 0);

    const fromRow = totalElements === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const toRow = Math.min(page * PAGE_SIZE, totalElements);

    return (
        <Box>
            {/* Legend — top */}
            <Box display="flex" alignItems="center" style={{ gap: 16, marginBottom: 12, flexWrap: "wrap" }}>
                {[
                    { rag: RAG.GREEN, label: "Current / done" },
                    { rag: RAG.AMBER, label: "Expiring ≤90 days" },
                    { rag: RAG.RED,   label: "Missing / overdue" },
                    { rag: RAG.GREY,  label: "Not set" },
                ].map(({ rag, label }) => (
                    <Box key={rag} display="flex" alignItems="center" style={{ gap: 5, fontSize: 12, color: "#6B7280" }}>
                        <span className={classes.dot} style={{ background: RAG_COLORS[rag].dot }} />
                        {label}
                    </Box>
                ))}
            </Box>

            {/* Summary tiles */}
            <Box display="flex" style={{ gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                <div className={classes.statCard} style={{ background: "#F3F4F6" }}>
                    <span className={classes.statValue} style={{ color: "#111827" }}>{rows.length}</span>
                    <span className={classes.statLabel} style={{ color: "#6B7280" }}>Staff members</span>
                </div>
                <div className={classes.statCard} style={{ background: "#dcfce7" }}>
                    <span className={classes.statValue} style={{ color: "#166534" }}>{countGreen}</span>
                    <span className={classes.statLabel} style={{ color: "#166534" }}>Items current</span>
                </div>
                <div className={classes.statCard} style={{ background: "#fef3c7" }}>
                    <span className={classes.statValue} style={{ color: "#92400e" }}>{countAmber}</span>
                    <span className={classes.statLabel} style={{ color: "#92400e" }}>Expiring ≤90 days</span>
                </div>
                <div className={classes.statCard} style={{ background: "#fee2e2" }}>
                    <span className={classes.statValue} style={{ color: "#991b1b" }}>{countRed}</span>
                    <span className={classes.statLabel} style={{ color: "#991b1b" }}>Missing / overdue</span>
                </div>
            </Box>

            {/* Info note */}
            <div className={classes.infoBox}>
                <i className="fas fa-info-circle" />
                Read-only. To upload or renew a document, open the staff profile →{" "}
                <strong>Documents</strong> tab.
            </div>

            {/* Filters */}
            <Box className={classes.filterRow}>
                <Box style={{ flex: 1, minWidth: 200 }}>
                    <InputSearch
                        onSearch={onSearch}
                        searchText={searchKey}
                        containerStyle={{ height: 41 }}
                    />
                </Box>
                <select
                    className={classes.select}
                    value={ragFilter}
                    onChange={e => onRagFilter(e.target.value)}
                >
                    <option value="">All statuses</option>
                    <option value={RAG.RED}>Red items only</option>
                    <option value={RAG.AMBER}>Amber items only</option>
                    <option value={RAG.GREEN}>Green only</option>
                </select>
            </Box>

            {loading && <LinearProgress style={{ marginBottom: 8 }} />}

            {/* Matrix table */}
            <div className={classes.wrapper}>
                <table className={classes.table}>
                    <thead>
                        <tr>
                            <th className={`${classes.th} ${classes.thSticky}`} style={{ textAlign: "left" }}>
                                Staff member
                            </th>
                            <th className={classes.th}>NDIS Screening</th>
                            {TRAINING_COLS.map(col => (
                                <th key={col.key} className={classes.th}>{col.label}</th>
                            ))}
                            <th className={classes.th}>Appraisal</th>
                            <th className={classes.th}>Overall RAG</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(({ worker, ndis, trainings, appraisal, overall }) => {
                            const rowBg = overall === RAG.RED ? "#fff8f8" : overall === RAG.AMBER ? "#fffbf0" : "#fff";
                            return (
                                <tr key={worker.id}>
                                    <td
                                        className={classes.tdSticky}
                                        style={{ background: rowBg }}
                                    >
                                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                                            <div className={classes.avatar}>
                                                {(worker.name || "").split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
                                            </div>
                                            <div>
                                                <Link
                                                    to={`/admin/workers/${worker.id}`}
                                                    style={{ fontSize: 12, color: "#0D9488", fontWeight: 500, textDecoration: "none" }}
                                                >
                                                    {worker.name}
                                                </Link>
                                                <div style={{ fontSize: 11, color: "#6B7280" }}>
                                                    {worker.workerType ? t(`worker_type.${worker.workerType}`) : "—"}
                                                </div>
                                            </div>
                                        </Box>
                                    </td>
                                    <td className={classes.td} style={{ background: rowBg }}>
                                        <RagCell rag={ndis.rag} label={ndis.label} classes={classes} />
                                    </td>
                                    {trainings.map((tr, i) => (
                                        <td key={i} className={classes.td} style={{ background: rowBg }}>
                                            <RagCell rag={tr.rag} label={tr.label} classes={classes} />
                                        </td>
                                    ))}
                                    <td className={classes.td} style={{ background: rowBg }}>
                                        <RagCell rag={appraisal.rag} label={appraisal.label} classes={classes} />
                                    </td>
                                    <td className={classes.td} style={{ background: rowBg }}>
                                        <OverallPill rag={overall} classes={classes} />
                                    </td>
                                </tr>
                            );
                        })}
                        {!loading && filtered.length === 0 && (
                            <tr>
                                <td
                                    colSpan={3 + TRAINING_COLS.length}
                                    style={{ padding: 24, textAlign: "center", color: "#6B7280" }}
                                >
                                    {t("label.no_data")}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination footer */}
            <Box display="flex" alignItems="center" justifyContent="flex-end" style={{ gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 12, color: "#6B7280", marginRight: 4 }}>
                    {fromRow}–{toRow} of {totalElements}
                </span>
                <button
                    onClick={() => goToPage(1)}
                    disabled={page === 1}
                    style={{
                        padding: "4px 8px", borderRadius: 4, border: "1px solid #E5E7EB",
                        background: "#fff", cursor: page === 1 ? "not-allowed" : "pointer",
                        color: page === 1 ? "#D1D5DB" : "#374151", fontSize: 13,
                    }}
                >«</button>
                <button
                    onClick={() => goToPage(page - 1)}
                    disabled={page === 1}
                    style={{
                        padding: "4px 8px", borderRadius: 4, border: "1px solid #E5E7EB",
                        background: "#fff", cursor: page === 1 ? "not-allowed" : "pointer",
                        color: page === 1 ? "#D1D5DB" : "#374151", fontSize: 13,
                    }}
                >‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                    .reduce((acc, p, i, arr) => {
                        if (i > 0 && p - arr[i - 1] > 1) acc.push("…");
                        acc.push(p);
                        return acc;
                    }, [])
                    .map((item, i) =>
                        item === "…" ? (
                            <span key={`ellipsis-${i}`} style={{ fontSize: 12, color: "#9CA3AF", padding: "0 2px" }}>…</span>
                        ) : (
                            <button
                                key={item}
                                onClick={() => goToPage(item)}
                                style={{
                                    padding: "4px 9px", borderRadius: 4, fontSize: 12,
                                    border: item === page ? "1px solid #0D9488" : "1px solid #E5E7EB",
                                    background: item === page ? "#0D9488" : "#fff",
                                    color: item === page ? "#fff" : "#374151",
                                    cursor: "pointer", fontWeight: item === page ? 600 : 400,
                                }}
                            >{item}</button>
                        )
                    )
                }
                <button
                    onClick={() => goToPage(page + 1)}
                    disabled={page === totalPages}
                    style={{
                        padding: "4px 8px", borderRadius: 4, border: "1px solid #E5E7EB",
                        background: "#fff", cursor: page === totalPages ? "not-allowed" : "pointer",
                        color: page === totalPages ? "#D1D5DB" : "#374151", fontSize: 13,
                    }}
                >›</button>
                <button
                    onClick={() => goToPage(totalPages)}
                    disabled={page === totalPages}
                    style={{
                        padding: "4px 8px", borderRadius: 4, border: "1px solid #E5E7EB",
                        background: "#fff", cursor: page === totalPages ? "not-allowed" : "pointer",
                        color: page === totalPages ? "#D1D5DB" : "#374151", fontSize: 13,
                    }}
                >»</button>
            </Box>
        </Box>
    );
};

export default ComplianceMatrixTab;
